'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { LayoutGroup, AnimatePresence, motion } from 'motion/react';
import type { HTMLMotionProps } from 'motion/react';
import { Icon } from '../../icons';
import './NumberCounter.css';

export type NumberCounterSize = 'sm' | 'md' | 'lg';

export interface NumberCounterProps extends Omit<HTMLMotionProps<'div'>, 'onChange'> {
  min?: number;
  max?: number;
  step?: number;
  size?: NumberCounterSize;
  disabled?: boolean;
  value?: number; // controlled
  defaultValue?: number; // uncontrolled
  onChange?: (value: number) => void;
  decreaseAriaLabel?: string;
  increaseAriaLabel?: string;
}

function useControlledNumber({
  value,
  defaultValue = 0,
  onChange,
}: Pick<NumberCounterProps, 'value' | 'defaultValue' | 'onChange'>) {
  const isControlled = value != null;
  const [internal, setInternal] = useState<number>(defaultValue);
  const current = isControlled ? (value as number) : internal;
  const set = useCallback(
    (next: number) => {
      if (!isControlled) setInternal(next);
      onChange?.(next);
    },
    [isControlled, onChange],
  );
  return [current, set] as const;
}

function AnimatedNumber({
  value,
  'aria-live': ariaLive,
}: {
  value: number;
  'aria-live'?: React.AriaAttributes['aria-live'];
}) {
  const previous = useRef<number>(value);
  const [direction, setDirection] = useState<1 | -1 | 0>(0);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const [height, setHeight] = useState<number>(16);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (measureRef.current) {
      const rect = measureRef.current.getBoundingClientRect();
      if (rect.height > 0) setHeight(rect.height);
      if (rect.width > 0) setWidth(rect.width);
    }
  }, []);

  useEffect(() => {
    const dir = value > previous.current ? 1 : value < previous.current ? -1 : 0;
    setDirection(dir as 1 | -1 | 0);
    previous.current = value;
    if (measureRef.current) {
      const rect = measureRef.current.getBoundingClientRect();
      if (rect.height > 0) setHeight(rect.height);
      if (rect.width > 0) setWidth(rect.width);
    }
  }, [value]);

  useEffect(() => {
    if (measureRef.current) {
      const rect = measureRef.current.getBoundingClientRect();
      if (rect.width > 0) setWidth(rect.width);
    }
  }, [value]);

  const offset = height;

  return (
    <motion.span
      ref={wrapperRef}
      aria-live={ariaLive}
      className="number-counter__number-wrapper"
      animate={{ width: width ?? undefined, height }}
      initial={false}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
      style={{ willChange: 'width, height' }}
      layout
    >
      {/* Hidden measurement element to lock width to current content */}
      <span
        ref={measureRef}
        className="number-counter__number"
        aria-hidden
        style={{ position: 'absolute', visibility: 'hidden' }}
      >
        {value}
      </span>
      <AnimatePresence initial={false} custom={direction}>
        <motion.span
          key={value}
          className="number-counter__number"
          initial={{ y: direction >= 0 ? offset : -offset }}
          animate={{ y: 0 }}
          exit={{ y: direction >= 0 ? -offset : offset }}
          transition={{ duration: 0.2, ease: 'easeInOut' }}
          style={{ fontVariantNumeric: 'tabular-nums', willChange: 'transform' }}
        >
          {value}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
}

export const NumberCounter: React.FC<NumberCounterProps> = ({
  min = Number.NEGATIVE_INFINITY,
  max = Number.POSITIVE_INFINITY,
  step = 1,
  size = 'md',
  disabled = false,
  value,
  defaultValue = 0,
  onChange,
  decreaseAriaLabel = 'Decrease',
  increaseAriaLabel = 'Increase',
  className,
  ...rest
}) => {
  const [current, setCurrent] = useControlledNumber({ value, defaultValue, onChange });

  const canDecrement = !disabled && current > min;
  const canIncrement = !disabled && current < max;

  const clamp = useCallback((next: number) => Math.min(Math.max(next, min), max), [min, max]);

  const handleAdjust = useCallback(
    (delta: number) => () => {
      if (disabled) return;
      const next = clamp(current + delta);
      if (next !== current) setCurrent(next);
    },
    [clamp, current, setCurrent, disabled],
  );

  const containerClass = `number-counter${className ? ` ${className}` : ''}`;

  return (
    <LayoutGroup>
      <motion.div
        {...rest}
        className={containerClass}
        data-size={size}
        data-disabled={disabled || undefined}
        layout
      >
        <motion.button
          type="button"
          className="number-counter__button"
          aria-label={decreaseAriaLabel}
          disabled={!canDecrement}
          onPointerDown={handleAdjust(-step)}
          whileHover={canDecrement ? { scale: 1.1 } : undefined}
          whileTap={canDecrement ? { scale: 0.9 } : undefined}
          whileFocus={{ boxShadow: '0 0 0 var(--border-width-md) var(--color-alias-info)' }}
          initial={{ boxShadow: '0 0 0 var(--border-width-md) transparent' }}
          transition={{ type: 'spring', stiffness: 600, damping: 35 }}
          layout
        >
          <Icon name="Minus" color="white" />
        </motion.button>

        <AnimatedNumber value={current} aria-live="polite" />

        <motion.button
          type="button"
          className="number-counter__button"
          aria-label={increaseAriaLabel}
          disabled={!canIncrement}
          onPointerDown={handleAdjust(step)}
          whileHover={canIncrement ? { scale: 1.1 } : undefined}
          whileTap={canIncrement ? { scale: 0.9 } : undefined}
          whileFocus={{ boxShadow: '0 0 0 var(--border-width-md) var(--color-alias-info)' }}
          initial={{ boxShadow: '0 0 0 var(--border-width-md) transparent' }}
          transition={{ type: 'spring', stiffness: 600, damping: 35 }}
          layout
        >
          <Icon name="Plus" color="white" />
        </motion.button>
      </motion.div>
    </LayoutGroup>
  );
};

NumberCounter.displayName = 'NumberCounter';
